import {
    useRef,
    useEffect,
    useState,
    useCallback,
    forwardRef,
    useImperativeHandle,
} from "react";
import { apiGetChunk, apiPost } from "./api";
import Spinner from "react-bootstrap/esm/Spinner";
import { Terminal as XTerm } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import { SearchAddon } from "@xterm/addon-search";

import "./Terminal.scss";
import "@xterm/xterm/css/xterm.css";

declare global {
    interface Window {
        executor: any;
    }
}

let controllers: { [key: string]: AbortController } = {};
const SIGNAL = new Uint8Array([0x01, 0x07]);

const API_CHUNK_URL = "/automation/api/executor/";
const API_STOP_JOB_URL = "/automation/api/stop_job/";

function removeSignal(data: Uint8Array, signal: Uint8Array): Uint8Array {
    let index = 0;
    while ((index = data.indexOf(signal[0], index)) !== -1) {
        if (data[index + 1] === signal[1]) {
            data = Uint8Array.from([
                ...data.slice(0, index),
                ...data.slice(index + signal.length),
            ]);
        } else index++;
    }
    return data;
}

const ansiColorsRegex = "/[\u001b\u009b][[()#;?]*(?:[0-9]{1,4}(?:;[0-9]{0,4})*)?[0-9A-ORZcf-nqry=><]/g;"

export interface TerminalProps {
    instance: string;
    params: any;
    onFinish?: (text: string) => void;
    onClickLinkCallback?: (link: string) => void;
    height?: string;
    apiGetChunkUrl?: string;
}

export type TerminalHandle = {
    execute: (props: TerminalProps) => void;
    abort: (needsApiCallToCancel?: boolean) => void;
    getTextContent: () => string;
};

export const Terminal = forwardRef<TerminalHandle, TerminalProps>((props, myRef) => {
    const ref = useRef<HTMLDivElement>(null);
    const terminal = useRef(
        new XTerm({
            convertEol: true,
            fontFamily: '"Commit Mono", ui-monospace, monospace',
            fontSize: 15,
            letterSpacing: 1,
            theme: {
                red: "#ff4757",
                brightRed: "#ff4757",
            },
            rightClickSelectsWord: true,
            disableStdin: true,
            scrollback: 9_999_999,
            linkHandler: {
                activate: (event, text) => {
                    if (props.onClickLinkCallback) {
                        event.preventDefault();
                        props.onClickLinkCallback(text);
                        return;
                    }
                    const url = text.trim().replace("https://localhost", "/automation/api/logs");
                    window.open(url, "_blank");
                },
            },
        })
    );

    const textContent = useRef<Uint8Array>(new Uint8Array());
    const fitAddon = useRef(new FitAddon());
    const searchAddon = useRef(new SearchAddon());

    // NEW: sticky-to-bottom state
    const stickToBottomRef = useRef(true);

    const [ready, setReady] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    // === SEARCH STATE ===
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [caseSensitive, setCaseSensitive] = useState(false);
    const [wholeWord, setWholeWord] = useState(false);
    const [regex, setRegex] = useState(false);
    const [optsOpen, setOptsOpen] = useState(false);

    const optsRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            if (!optsRef.current) return;
            if (!optsRef.current.contains(e.target as Node)) setOptsOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const getContent = (): string =>
        new TextDecoder().decode(textContent.current).replace(ansiColorsRegex, "");

    const isViewportAtBottom = () => {
        const el = ref.current?.querySelector(".xterm-viewport") as HTMLElement | null;
        if (!el) return true;
        return Math.abs(el.scrollTop + el.clientHeight - el.scrollHeight) < 2;
    };

    const execute = useCallback(async (callProps: TerminalProps) => {
        if (callProps.params && ready) {
            textContent.current = new Uint8Array();
            terminal.current.clear();
            // Reset sticky mode on new run
            stickToBottomRef.current = true;

            controllers[callProps.instance] = new AbortController();
            setLoading(true);
            setError("");
            apiGetChunk(callProps.apiGetChunkUrl || API_CHUNK_URL, {
                params: callProps.params,
                signal: controllers[callProps.instance].signal,
                onProgress(data: Uint8Array) {
                    data = removeSignal(data, SIGNAL);
                    if (data.length === 0) return;

                    if (textContent.current.length > 100 * 1024 * 1024) {
                        textContent.current = new Uint8Array([
                            ...textContent.current.slice(-1024),
                            ...data,
                        ]);
                    } else {
                        textContent.current = new Uint8Array([
                            ...textContent.current,
                            ...data,
                        ]);
                    }

                    const term = terminal.current;
                    if (term) {
                        term.write(data);
                        // Only auto-scroll when user is at bottom
                        if (stickToBottomRef.current) term.scrollToBottom();
                    }
                },
            })
                .then(() => {
                    setLoading(false);
                    if (callProps.onFinish) callProps.onFinish(getContent());
                })
                .catch(() => setLoading(false));
        }
    }, [ready]);

    const stopJob = () => {
        if (props.params)
            apiPost<any>(API_STOP_JOB_URL, props.params)
                .then((result) => setMessage(result.message))
                .catch((err) => setError(err.message));
    };

    useEffect(() => {
        let divRef: HTMLDivElement | null = null;
        let term: XTerm | null = null;
        let observer: ResizeObserver | null = null;

        // NEW: listeners cleanup holders
        let viewportEl: HTMLElement | null = null;
        let disposableScroll: { dispose: () => void } | null = null;

        if (ref.current) {
            terminal.current.loadAddon(fitAddon.current);
            terminal.current.loadAddon(searchAddon.current);
            terminal.current.open(ref.current);

            // Detect user scroll to toggle sticky mode
            term = terminal.current;
            viewportEl = ref.current.querySelector(".xterm-viewport") as HTMLElement | null;

            const onTermScroll = () => {
                stickToBottomRef.current = isViewportAtBottom();
            };
            const onViewportScroll = () => {
                stickToBottomRef.current = isViewportAtBottom();
            };

            disposableScroll = term.onScroll(onTermScroll);
            viewportEl?.addEventListener("scroll", onViewportScroll, { passive: true });

            observer = new ResizeObserver((entries) => {
                if (entries[0].contentRect.width) {
                    fitAddon.current.fit();
                    // keep bottom only if sticky
                    if (stickToBottomRef.current) terminal.current.scrollToBottom();
                    setReady(true);
                }
            });
            observer.observe(ref.current);
            divRef = ref.current;
        }

        return () => {
            disposableScroll?.dispose();
            viewportEl?.removeEventListener("scroll", () => { });
            term?.dispose();
            divRef && observer?.unobserve(divRef);
        };
    }, []);

    useEffect(() => {
        if (ready) {
            if (props.params) terminal.current.clear();
            execute(props);
        }
        // eslint-disable-next-line
    }, [execute, props.params, ready]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            controllers[props.instance] && controllers[props.instance].abort();
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [props.instance]);

    const doFindNext = useCallback(
        (term?: string) => {
            const t = (term ?? searchTerm).trim();
            if (!t) return;
            searchAddon.current.findNext(t, { caseSensitive, regex, wholeWord });
        },
        [searchTerm, caseSensitive, regex, wholeWord, searchAddon]
    );

    const doFindPrev = useCallback(() => {
        const t = searchTerm.trim();
        if (!t) return;
        searchAddon.current.findPrevious(t, { caseSensitive, regex, wholeWord });
    }, [searchTerm, caseSensitive, regex, wholeWord, searchAddon]);

    const clearSearch = () => searchAddon.current.clearDecorations();

    // === GLOBAL HOTKEYS ===
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            // Ctrl/Cmd + F — open search and block browser find
            if ((e.ctrlKey || e.metaKey) && e.code === "KeyF") {
                e.preventDefault();
                e.stopPropagation();
                setSearchOpen(true);
                setTimeout(() => inputRef.current?.focus(), 0);
                return;
            }

            // ESC — close search (if open)
            if (e.code === "Escape" && searchOpen) {
                e.preventDefault();
                e.stopPropagation();
                setSearchOpen(false);
                clearSearch();
                return;
            }

            // F3 and Shift+F3 — next/prev
            if (e.code === "F3" && searchOpen) {
                e.preventDefault();
                e.stopPropagation();
                if (e.shiftKey) doFindPrev(); else doFindNext();
                return;
            }

            // Enter — go next (when search is open and focus NOT in input)
            if (e.code === "Enter" && searchOpen && document.activeElement !== inputRef.current) {
                e.preventDefault();
                e.stopPropagation();
                if (e.shiftKey) doFindPrev(); else doFindNext();
                return;
            }

            // Arrow keys — navigate results when search is open
            if (searchOpen && (e.code === "ArrowDown" || e.code === "ArrowRight")) {
                e.preventDefault();
                e.stopPropagation();
                doFindNext();
                return;
            }
            if (searchOpen && (e.code === "ArrowUp" || e.code === "ArrowLeft")) {
                e.preventDefault();
                e.stopPropagation();
                doFindPrev();
                return;
            }

            if ((e.ctrlKey || e.metaKey) && e.code === "KeyC") {
                if (document.activeElement === inputRef.current) return;
                const ae = document.activeElement as HTMLElement | null;
                const isXtermTextarea = ae?.classList?.contains("xterm-helper-textarea");
                const selection = terminal.current?.getSelection?.() ?? "";
                if (selection && (isXtermTextarea || ae === document.body || !ae?.isContentEditable)) {
                    e.preventDefault();
                    e.stopPropagation();
                    navigator.clipboard?.writeText(selection).catch(() => {
                        const ta = document.createElement("textarea");
                        ta.value = selection;
                        ta.style.position = "fixed";
                        ta.style.opacity = "0";
                        document.body.appendChild(ta);
                        ta.focus();
                        ta.select();
                        try { document.execCommand("copy"); } catch { }
                        document.body.removeChild(ta);
                    });
                    return;
                }
            }

        };

        window.addEventListener("keydown", onKeyDown, true);
        return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [searchOpen, searchTerm, caseSensitive, wholeWord, regex, doFindNext, doFindPrev]);

    useImperativeHandle(myRef, () => ({
        execute: (p: TerminalProps) => {
            terminal.current.clear();
            stickToBottomRef.current = true;
            execute(p);
        },
        abort: (needsApiCallToCancel: boolean = true) => {
            if (needsApiCallToCancel) stopJob();
            else setMessage("Задача успешно остановлена");
            controllers[props.instance]?.abort();
            delete controllers[props.instance];
        },
        getTextContent: () => getContent(),
    }));

    return (
        <div
            className="terminal"
            style={{ height: props.height || "100%", width: "100%", position: "relative" }}
        >
            {/* SEARCH OVERLAY */}
            <div
                className="terminal-search-overlay"
                role="search"
                aria-label="Поиск по терминалу"
                aria-hidden={!searchOpen}
                style={{ display: searchOpen ? "block" : "none" }}
                onKeyDown={(e) => {
                    if (e.code === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                        doFindNext();
                    }
                }}
            >
                <div className="terminal-search" ref={optsRef}>
                    <input
                        ref={inputRef}
                        onKeyDown={(e) => { if (e.code === "Enter" || e.code === "NumpadEnter") { e.preventDefault(); e.stopPropagation(); doFindNext(); } }}
                        className="terminal-search__input"
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Поиск…"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck={false}
                    />

                    <div className="terminal-dropdown">
                        <button
                            type="button"
                            className="terminal-search__btn terminal-dropdown__toggle"
                            aria-label="Опции поиска"
                            aria-haspopup="menu"
                            aria-expanded={optsOpen}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setOptsOpen(v => !v);
                            }}
                        >
                            <i className="fal fa-sliders-h" />
                        </button>

                        {optsOpen && (
                            <div className="terminal-dropdown__menu" role="menu">
                                <button
                                    type="button"
                                    role="menuitemcheckbox"
                                    aria-checked={caseSensitive}
                                    className={`terminal-dropdown__item ${caseSensitive ? "is-active" : ""}`}
                                    onClick={() => setCaseSensitive(v => !v)}
                                >
                                    <i className={`far ${caseSensitive ? "fa-check-square" : "fa-square"}`} />
                                    <span className="terminal-dropdown__label">Учитывать регистр (Aa)</span>
                                </button>
                                <button
                                    type="button"
                                    role="menuitemcheckbox"
                                    aria-checked={wholeWord}
                                    className={`terminal-dropdown__item ${wholeWord ? "is-active" : ""}`}
                                    onClick={() => setWholeWord(v => !v)}
                                >
                                    <i className={`far ${wholeWord ? "fa-check-square" : "fa-square"}`} />
                                    <span className="terminal-dropdown__label">Целое слово (Word)</span>
                                </button>
                                <button
                                    type="button"
                                    role="menuitemcheckbox"
                                    aria-checked={regex}
                                    className={`terminal-dropdown__item ${regex ? "is-active" : ""}`}
                                    onClick={() => setRegex(v => !v)}
                                >
                                    <i className={`far ${regex ? "fa-check-square" : "fa-square"}`} />
                                    <span className="terminal-dropdown__label">Регулярное выражение (.*)</span>
                                </button>
                            </div>
                        )}
                    </div>

                    <button type="button" className="terminal-search__btn" title="Назад" onClick={doFindPrev}>
                        <i className="fal fa-chevron-up" />
                    </button>
                    <button type="button" className="terminal-search__btn" title="Вперёд" onClick={() => doFindNext()}>
                        <i className="fal fa-chevron-down" />
                    </button>
                    <button
                        type="button"
                        className="terminal-search__btn"
                        title="Закрыть (Esc)"
                        aria-label="Закрыть поиск"
                        onClick={() => { setSearchOpen(false); clearSearch(); }}
                    >
                        <i className="fal fa-times" />
                    </button>
                </div>
            </div>

            {error && <div className="alert-danger terminal-error">{error}</div>}
            {message && <div className="alert alert-success terminal-error">{message}</div>}
            {loading && <Spinner animation="border" className="terminal-spinner" />}

            <div className="terminal-div" ref={ref}></div>
        </div>
    );
});
